import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import { requestPublicProviderSettings } from '@/services/public-request';
import type { ProviderCheckoutContentSettings, UpdateProviderCheckoutContentInput } from '@/types/provider-checkout-content';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

function normalizeField(value: unknown): string | null {
  const raw = readString(value)?.trim() ?? '';
  return raw.length > 0 ? raw : null;
}

function normalizeBullets(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => readString(item)?.trim() ?? '')
    .filter((item) => item.length > 0)
    .slice(0, 5);
}

function normalizeSettings(payload: ApiPayload | undefined): ProviderCheckoutContentSettings {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});

  const bulletsFromRecord = normalizeBullets(record?.bullets);
  const hasBulletsField =
    record &&
    typeof record === 'object' &&
    !Array.isArray(record) &&
    'bullets' in record;

  return {
    headline: normalizeField(record?.headline ?? record?.title),
    subheadline: normalizeField(record?.subheadline ?? record?.subtitle),
    bullets: hasBulletsField
      ? bulletsFromRecord
      : [
          normalizeField(record?.bullet_1) ?? '',
          normalizeField(record?.bullet_2) ?? '',
          normalizeField(record?.bullet_3) ?? '',
        ].filter((item) => item.length > 0),
  };
}

export class ProviderCheckoutContentRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestProviderCheckoutContent(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderCheckoutContentRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderCheckoutContentRequestError('Provider checkout content request failed', undefined, error);
  }
}

async function requestPublicCheckoutContent(
  path: string,
  options: { method?: 'GET'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestPublicProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderCheckoutContentRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderCheckoutContentRequestError('Provider checkout content request failed', undefined, error);
  }
}

class ProviderCheckoutContentService {
  async getSettings(): Promise<ProviderCheckoutContentSettings> {
    try {
      const response = await requestProviderCheckoutContent('/api/v1/provider/settings/checkout-content');
      return normalizeSettings(response);
    } catch (error) {
      if (error instanceof ProviderCheckoutContentRequestError && (error.status ?? 0) === 404) {
        return { headline: null, subheadline: null, bullets: [] };
      }
      throw error;
    }
  }

  async getPublicCopy(): Promise<ProviderCheckoutContentSettings | null> {
    try {
      const response = await requestPublicCheckoutContent('/api/v1/public/checkout-content');
      return normalizeSettings(response);
    } catch (error) {
      if (error instanceof ProviderCheckoutContentRequestError) {
        const status = error.status ?? 0;
        if (status === 401 || status === 403 || status === 404) {
          return null;
        }
      }
      throw error;
    }
  }

  async updateSettings(input: UpdateProviderCheckoutContentInput): Promise<ProviderCheckoutContentSettings> {
    const body = {
      headline: input.headline,
      subheadline: input.subheadline,
      bullets: input.bullets,
    };

    const response = await requestProviderCheckoutContent('/api/v1/provider/settings/checkout-content', {
      method: 'PUT',
      body,
    });

    return normalizeSettings(response);
  }
}

export const providerCheckoutContentService = new ProviderCheckoutContentService();

export { normalizeField as normalizeCheckoutCopyField, normalizeBullets as normalizeCheckoutBullets };
